import { useEffect } from "react";
import { toastService } from "../../../providers";
import {
  useCreateMerchantInstitution,
  useEditMerchantInstitution,
  useGetMerchantProfileById,
} from "../../../hooks/queries/useMerchantQueries";
import { ALLOWED_EDIT_FIELDS } from "../config/merchantFieldConfig";
import { extractAxiosErrorMessage } from "../../../services/utils/errorUtils";

export const useMerchantFormSubmit = ({
  merchantForm,
  modalMode,
  selectedMerchant,
  closeModal,
}) => {
  const {
    mutate: merchantInstitutionCreate,
    isPending: merchantInstitutionCreating,
  } = useCreateMerchantInstitution(); 

  const {
    mutate: merchantInstitutionUpdate,
    isPending: merchantInstitutionUpdating,
  } = useEditMerchantInstitution();

  const {
    data: merchantDetails,
    isPending: merchantDetailsLoading,
    isError: merchantDetailsError,
  } = useGetMerchantProfileById(
    modalMode === "edit" && selectedMerchant
      ? selectedMerchant?.merchantId
      : null,
  );

  useEffect(() => {
    if (merchantDetails && modalMode === "edit") {
      const success = merchantForm.populateFromDetails(merchantDetails);
      if (!success) {
        toastService.error("Failed to load merchant details for editing");
      }
    }
  }, [merchantDetails, modalMode]);

  const handleSubmit = merchantForm.handleSubmit(async (values) => {
    if (modalMode === "create") {
      const payload = { ...values };
      merchantInstitutionCreate(payload, {
        onSuccess: () => {
          toastService.success("Merchant institution created successfully!");
          closeModal();
        },

        onError: (err) => {
          toastService.error(extractAxiosErrorMessage(err, "Failed to create merchant institution"));
        },
      });
    } else if (modalMode === "edit" && selectedMerchant) {
      const updatePayload = Object.fromEntries(
        Object.entries(values).filter(([key]) =>
          ALLOWED_EDIT_FIELDS.includes(key),
        ),
      );
      merchantInstitutionUpdate(
        {
          merchantId: selectedMerchant.merchantId,
          merchantData: updatePayload,
        },
        {
          onSuccess: () => {
            toastService.success("Merchant institution updated successfully!");
            closeModal();
          },

          onError: (err) => {
            toastService.error(extractAxiosErrorMessage(err, "Failed to update merchant institution"));
          },
        },
      );
    }
  });

  const isSubmitting =
    merchantInstitutionCreating || merchantInstitutionUpdating;

  return {
    handleSubmit,
    isSubmitting,
    merchantDetailsLoading,
    merchantDetailsError,
    merchantInstitutionCreating,
    merchantInstitutionUpdating,
  };
};

export default useMerchantFormSubmit;
